import type { EscrowPayoutRoleSummary, EscrowPayouts, PayoutComponent, PayoutStatus } from 'api/escrow/types';

// -- display label maps (single source of truth) --

export const PAYOUT_COMPONENT_LABELS: Record<PayoutComponent, string> = {
  buyer_deposit_forfeit: 'Buyer deposit forfeiture',
  buyer_deposit_return: 'Buyer deposit return',
  partial_refund: 'Partial refund',
  partial_release: 'Partial release',
  purchase_price: 'Purchase price release',
  purchase_price_refund: 'Purchase price refund',
  seller_deposit_forfeit: 'Seller deposit forfeiture',
  seller_deposit_return: 'Seller deposit return',
  unknown: 'Payout',
};

export const PAYOUT_STATUS_LABELS: Record<PayoutStatus, string> = {
  accepted: 'Processing',
  failed: 'Failed',
  pending: 'Pending',
  reversed: 'Reversed',
  succeeded: 'Completed',
};

export const PAYOUT_STATUS_COLORS: Record<PayoutStatus, string> = {
  accepted: 'text-blue-600',
  failed: 'text-brand-red-main',
  pending: 'text-yellow-600',
  reversed: 'text-brand-red-main',
  succeeded: 'text-green-600',
};

export const PAYOUT_ROLE_STATUS_COLORS: Record<EscrowPayoutRoleSummary['status'], string> = {
  failed: 'bg-red-100 text-red-700',
  pending: 'bg-yellow-100 text-yellow-700',
  succeeded: 'bg-green-100 text-green-700',
};

export const PAYOUT_ROLE_STATUS_LABELS: Record<EscrowPayoutRoleSummary['status'], string> = {
  failed: 'Needs attention',
  pending: 'Processing',
  succeeded: 'Completed',
};

// -- failure code labels (user-friendly) --

const FAILURE_CODE_LABELS: Record<string, string> = {
  INSUFFICIENT_BALANCE: 'Insufficient balance',
  INVALID_DESTINATION: 'Invalid destination',
  PAYOUT_EXPIRED: 'Payout expired',
  PAYOUT_REVERSED: 'Payout reversed',
};

/** Returns a user-friendly label for a payout failure code, or the raw code if unmapped. */
export function getFailureCodeLabel(failureCode: string | null): string | null {
  if (!failureCode) return null;
  return FAILURE_CODE_LABELS[failureCode] ?? failureCode;
}

// -- terminal status check --

const TERMINAL_PAYOUT_STATUSES = new Set<PayoutStatus>(['failed', 'reversed', 'succeeded']);

/** Returns true when a payout status is terminal (no further changes expected). */
export function isTerminalPayoutStatus(status: PayoutStatus): boolean {
  return TERMINAL_PAYOUT_STATUSES.has(status);
}

// -- settlement badge derivation (Step 2) --

export type SettlementBadge = {
  colour: string;
  label: string;
};

/**
 * Derives a settlement badge from the payout state of a completed trade.
 *
 * Only meaningful when the trade is already in Completed status — callers
 * should guard accordingly.
 */
export function getSettlementBadge(payouts?: EscrowPayouts): SettlementBadge {
  if (!payouts) {
    return { colour: 'bg-green-100 text-green-700', label: 'Settlement completed' };
  }

  const roles = [payouts.buyer, payouts.seller].filter(Boolean) as EscrowPayoutRoleSummary[];

  if (roles.length === 0) {
    return { colour: 'bg-green-100 text-green-700', label: 'Settlement completed' };
  }

  const allSucceeded = roles.every((r) => r.status === 'succeeded');
  if (allSucceeded) {
    return { colour: 'bg-green-100 text-green-700', label: 'Settlement and payout completed' };
  }

  const anyFailed = roles.some((r) => r.status === 'failed');
  if (anyFailed) {
    return { colour: 'bg-red-100 text-red-700', label: 'Settlement completed, payout needs attention' };
  }

  // pending or accepted
  return { colour: 'bg-yellow-100 text-yellow-700', label: 'Settlement completed, payout processing' };
}

/**
 * Returns 'new' if payout data uses per-component breakdown, 'old' if the
 * backward-compat pseudo-component fallback was applied (single unknown
 * component per role).
 */
export function getPayoutShapeVersion(payouts: EscrowPayouts): 'new' | 'old' {
  const roles = [payouts.buyer, payouts.seller].filter(Boolean) as EscrowPayoutRoleSummary[];
  const isOld =
    roles.length > 0 && roles.every((r) => r.components.length === 1 && r.components[0].component === 'unknown');
  return isOld ? 'old' : 'new';
}

/** Returns true when all payout components across all roles are terminal. */
export function allPayoutsTerminal(payouts?: EscrowPayouts): boolean {
  if (!payouts) return true;

  const roles = [payouts.buyer, payouts.seller].filter(Boolean) as EscrowPayoutRoleSummary[];
  if (roles.length === 0) return true;

  return roles.every((r) => r.components.every((c) => isTerminalPayoutStatus(c.status)));
}
